<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, computed } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import { Grid, type GridSortChangeEvent, type GridPageChangeEvent } from '@progress/kendo-vue-grid';
import type { IOrderListItem } from '../../types/OrderTypes'
import type { SortDescriptor } from '@progress/kendo-data-query'
import { orderBy } from '@progress/kendo-data-query';
import { formatDate } from "../../api/Common.ts";
import { FormatCurrency } from '../../services/currencyFormatter'
import { loadMessages, LocalizationProvider } from '@progress/kendo-vue-intl'

import { GetBeeNumberAuthenicatedUser, GetOrderReceiptUrl, GetRMAEntryUrl } from '../../stores/SettingsStore.ts'

import { BatchGetList } from '../../services/BatchService.ts'
import { LiveOrderGetList, PaidOrderGetList } from '../../services/OrderService.ts'

import { handleError } from '../../services/ErrorService.ts'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'

import { saveExcel } from '@progress/kendo-vue-excel-export';
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Drawer, DrawerItem } from '@progress/kendo-vue-layout'
import { DropDownList } from '@progress/kendo-vue-dropdowns';
import { ListBox, processListBoxDragAndDrop, type ListBoxDragEvent } from '@progress/kendo-vue-listbox'
import {
  DatePicker,
  type DatePickerChangeEvent,
} from '@progress/kendo-vue-dateinputs'
import { useDatePickerLocalization } from '../../composables/useDatePickerLocalization'
import { SvgIcon } from '@progress/kendo-vue-common'
import { TextBox as KTextBox, NumericTextBox as KNumericTextBox, Checkbox as KCheckBox,  type CheckboxChangeEvent  } from '@progress/kendo-vue-inputs'
import { moreVerticalIcon, tableColumnGroupsIcon, arrowsTopBottomIcon, filterIcon, fileTxtIcon, redoIcon, xIcon } from '@progress/kendo-svg-icons'

const logger = useLogger()
setStoreLogger(logger)

const { dateFormat, formatPlaceholder } = useDatePickerLocalization(locale)

interface Batch {
  text: string
  id: string
}

interface OrderType {
  text: string
  id: string
}

interface GridColumn {
  text: string
  id: string
  type: string
}

interface GridColumnOperand {
  text: string
  id: string
}

const searchQuery = ref('')
const searchText = ref<string>('')
const searchNumber = ref<number | undefined>(undefined)
const searchSecondNumber = ref<number | undefined>(undefined)
const txtDate = ref<Date | undefined>(undefined)

// Debounced search handler
function onSearchInput() {
  // No need for debounce since we're using computed properties
  // The computed properties will automatically update when searchQuery changes
}
const txtSecondDate = ref<Date | undefined>(undefined)
const batchDDL = ref<Batch[]>([])
const selectedBatch = ref<Batch>({ text: '', id: '' })
const orderTypeDDL = ref<OrderType[]>([])
const selectedOrderType = ref<OrderType>({ text: '', id: '' })
const columnDDL = ref<GridColumn[]>([])
const selectedColumn = ref<GridColumn>({ text: '', id: '', type: '' })
const columnOperandDDL = ref<GridColumnOperand[]>([])
const selectedColumnOperand = ref<GridColumnOperand>({ text: '', id: '' })
const columns = ref([
        {
          field: 'aoeOrderId',
          title: t('orderList.OrderId'),
          width: resolveColumnWidth('120px', t('orderList.OrderId')),
          cell: 'orderIDTemplate',
          hidden: false
        },
        {
          field: 'aoeMasterOrderId',
          title: t('orderList.MasterOrderId'),
          width: resolveColumnWidth('140px', t('orderList.MasterOrderId')),
          hidden: false
        },
        {
          field: 'total',
          title: t('orderList.OrderTotal'),
          width: resolveColumnWidth('120px', t('orderList.OrderTotal')),
          cell: 'orderTotalTemplate',
          hidden: false
        },
        {
          field: 'orderTime',
          title: t('orderList.OrderDate'),
          width: resolveColumnWidth('140px', t('orderList.OrderDate')),
          cell: 'orderDateTemplate',
          hidden: false
        },
        {
          field: 'commissionableValue',
          title: t('orderList.CommissionableValue'),
          width: resolveColumnWidth('150px', t('orderList.CommissionableValue')),
          cell: 'orderCommissionableValueTemplate',
          hidden: false
        },
        {
          field: 'trackingNumber',
          title: t('orderList.TrackingNumber'),
          width: resolveColumnWidth('160px', t('orderList.TrackingNumber')),
          cell: 'orderTrackingNumberTemplate',
          hidden: false
        },
        {
          field: 'orderTypeText',
          title: t('orderList.OrderType'),
          width: resolveColumnWidth('120px', t('orderList.OrderType')),
          hidden: false
        },
        {
          field: 'customer.lastName',
          title: t('orderList.LastName'),
          width: resolveColumnWidth('140px', t('orderList.LastName')),
          hidden: false
        },
        {
          field: 'customer.email',
          title: t('orderList.Email'),
          width: resolveColumnWidth('200px', t('orderList.Email')),
          hidden: false
        },
        {
          field: 'actions',
          title: ' ',
          width: 80,
          cell: 'orderReturnTemplate',
          headerClassName: 'order-grid-return-header',
          headerCell: 'actionsHeaderTemplate',
          hidden: false
        }
      ])
let customizeColumns = columns.value
// Actions popup menu state
const showActionsMenu = ref(false)
const activeOrderId = ref<string>('')
const activeOrderToken = ref<string>('')
const menuPosition = ref({ x: 0, y: 0 })

const finalOrderSummaryURL = ref<string>('')

// Active filter indicator state
interface FilterItem {
  id: string;
  type: string;
  text: string;
}

const hasActiveFilter = computed(() => {
  return selectedBatch.value.id !== '' ||
         selectedOrderType.value.id !== '' ||
         (selectedColumn.value.id !== '' && hasColumnFilterValue())
})

const activeFilters = computed(() => {
  const filters: FilterItem[] = []

  // Batch filter
  if (selectedBatch.value.id === 'paid') {
    filters.push({
      id: 'batch',
      type: 'batch',
      text: t('orderList.PaidView')
    })
  } else if (selectedBatch.value.id === 'unpaid') {
    filters.push({
      id: 'batch',
      type: 'batch',
      text: t('orderList.UnpaidView')
    })
  } else if (selectedBatch.value.id !== '') {
    filters.push({
      id: 'batch',
      type: 'batch',
      text: selectedBatch.value.text
    })
  }

  // Order type filter
  if (selectedOrderType.value.id !== '') {
    filters.push({
      id: 'orderType',
      type: 'orderType',
      text: selectedOrderType.value.text
    })
  }

  // Column filter
  if (selectedColumn.value.id !== '' && selectedColumnOperand.value.id !== '') {
    const columnName = selectedColumn.value.text
    const operand = selectedColumnOperand.value.text
    let value = ''

    if (selectedColumn.value.id === 'orderTime' && txtDate.value) {
      value = txtDate.value.toLocaleDateString()
      if (selectedColumnOperand.value.id === 'Between' && txtSecondDate.value) {
        value += ' - ' + txtSecondDate.value.toLocaleDateString()
      }
    } else if (searchText.value) {
      value = searchText.value
      if (selectedColumnOperand.value.id === 'Between' && searchSecondNumber.value !== undefined) {
        value += ' - ' + searchSecondNumber.value
      }
    } else if (searchNumber.value !== null && searchNumber.value !== undefined) {
      value = String(searchNumber.value)
      if (selectedColumnOperand.value.id === 'Between' && searchSecondNumber.value !== undefined) {
        value += ' - ' + searchSecondNumber.value
      }
    }

    if (value) {
      filters.push({
        id: 'column',
        type: 'column',
        text: `${columnName} ${operand} ${value}`
      })
    }
  }

  return filters
})

const skip = ref(0);
const take = ref(20);
const loader = ref<boolean>(false)
const sort = ref<SortDescriptor[]>([{ field: 'orderTime', dir: 'desc' }])
const gridData = ref<IOrderListItem[]>([])
const paidGridData = ref<IOrderListItem[]>([])
const paidOrdersLoaded = ref(false)
const paidOrdersFailed = ref(false)
let paidOrdersRequest: Promise<void> | null = null
const isFiltered = ref(false)
const sortedGridData = computed<IOrderListItem[]>(() => {
  // Only use gridData once a filter has actually been applied. Keying off
  // gridData.length would make a filter that matches nothing fall back to the
  // full list, which reads as "the filter did nothing".
  let data = isFiltered.value ? gridData.value : origGridData.value

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    data = data.filter((item: IOrderListItem) => {
      return (
        item.aoeOrderId?.toString().toLowerCase().includes(query) ||
        item.aoeMasterOrderId?.toString().toLowerCase().includes(query) ||
        (item.customerName?.toString().toLowerCase() || '').includes(query) ||
        item.customerNumber?.toString().toLowerCase().includes(query) ||
        item.customer?.lastName?.toString().toLowerCase().includes(query) ||
        item.customer?.email?.toString().toLowerCase().includes(query) ||
        item.orderTypeText?.toString().toLowerCase().includes(query)
      )
    })
  }
  return orderBy(data, sort.value)
});
const pageSortedGridData = computed(() => sortedGridData.value.slice(skip.value, take.value + skip.value));
const total = computed(() => sortedGridData.value.length);
const origGridData = ref<IOrderListItem[]>([]);
const expanded = ref(false)
const filterExpanded = ref(false)
const drawerItems = ref([
  {
    text: t('orderList.ColumnList'),
    selected: false,
    data: { path: '/' },
  },
])
const filterDrawerItems = ref([
  {
    text: t('orderList.FilterList'),
    selected: false,
    data: { path: '/' },
  },
])

const draggedItem = ref({});
const gridKey = ref(0);


function pageChangeHandler(event: GridPageChangeEvent) {
    loader.value = true;
    setTimeout(() => {
        loader.value = false;
        skip.value = event.page.skip;
        take.value = event.page.take;
    }, 300);
}

// Load Kendo Grid messages based on current locale
function loadKendoMessages() {
  const currentLocale = locale.value

  // Wait for next tick to ensure locale change is applied
  setTimeout(() => {
    // Kendo expects the messages in a specific flat format with dot notation
    const messages = {
      'grid.pagerItemsPerPage': t('kendo.grid.pagerItemsPerPage'),
      'grid.pagerInfo': t('kendo.grid.pagerInfo'),
      'grid.noRecords': t('kendo.grid.noRecords')
    }

    loadMessages(messages, currentLocale)

    // Force grid refresh to apply new messages
    gridKey.value++
  }, 0)
}

onMounted(async () => {
  logger.debug('onMounted AoeOrderList.vue')

  loadKendoMessages()

  // Get the order receipt URL from SettingsStore
  finalOrderSummaryURL.value = await GetOrderReceiptUrl()

  const columnAll = { text: t('orderList.NoColumnSelected'), id: '', type: '' }
  columnDDL.value.push(columnAll)
  selectedColumn.value = columnAll

  for (const column of columns.value) {
    if (column.field === 'actions' || column.field === 'orderTypeText') {
      continue; // Skip the actions column as it is handled separately
    }

    let columnType = 'string';
    if (column.field == "orderTime")
    {
      columnType = 'date';
    }
    else if (column.field == "total" || column.field == "aoeOrderId" || column.field == "aoeMasterOrderId")
    {
      columnType = 'number';
    }

    columnDDL.value.push({ text: column.title, id: column.field, type: columnType })
  }

  SetColumnOperandDDLValues()

  // '' spans both the live and the paid lists; selecting it clears the view filter
  batchDDL.value.push({ text: t('orderList.AllViews'), id: '' })

  const unPaidView = { text: t('orderList.UnpaidView'), id: 'unpaid' }
  batchDDL.value.push(unPaidView)
  selectedBatch.value = unPaidView

  batchDDL.value.push({ text: t('orderList.PaidView'), id: 'paid' })

  BatchGetList().then((batchResp) => {
      if (batchResp)
      {
        for (const batch of batchResp.items) {
            batchDDL.value.push({ text: batch.batchName, id: String(batch.appPeriodDateGuid) })
        }
      }
    }).catch(async error => {
      await handleError(error, t)
    })

  LoadGridData()
})

async function LoadGridData()
{
  const beeNumberAuth = await GetBeeNumberAuthenicatedUser()
  orderTypeDDL.value = []

  const orderTypeAll = { text: t('orderList.AllOrderTypes'), id: '' }
  orderTypeDDL.value.push(orderTypeAll)
  selectedOrderType.value = orderTypeAll
  if (beeNumberAuth != null)
  {
    ReLoadGrid()
    LiveOrderGetList(beeNumberAuth).then((orderResp) => {
      if (orderResp)
      {
        // One assignment instead of a push per row: pushing thousands of items
        // into a reactive array fires reactivity each time and blocks the main
        // thread. origGridData is the only owner of the unfiltered list, so a
        // response arriving late can no longer undo a filter the user applied.
        origGridData.value = orderResp.items
        for (const aoeOrder of orderResp.items) {
            if (orderTypeDDL.value.findIndex(x => x.id === aoeOrder.orderTypeId) < 0) {
                orderTypeDDL.value.push({ text: aoeOrder.orderTypeText, id: aoeOrder.orderTypeId })
            }
        }
        loader.value = false;
        HideNoRecordsWhileLoading()
      }
    }).catch(async error => {
      await handleError(error, t)
    })
  }
}

// The paid orders endpoint is slow enough to time out, so it is fetched only
// when a filter actually needs it instead of on every page load.
async function LoadPaidGridData()
{
  const beeNumberAuth = await GetBeeNumberAuthenicatedUser()
  if (beeNumberAuth == null) return

  loader.value = true
  HideNoRecordsWhileLoading()
  paidOrdersFailed.value = false

  try {
    const orderResp = await PaidOrderGetList(beeNumberAuth)
    if (orderResp) paidGridData.value = orderResp.items
    paidOrdersLoaded.value = true
  }
  catch (error) {
    paidOrdersFailed.value = true
    // Drop the cached request so the next filter that needs paid orders retries
    paidOrdersRequest = null
    await handleError(error, t)
  }
  finally {
    loader.value = false
    HideNoRecordsWhileLoading()
  }
}

// Only the default 'unpaid' view is served entirely by the live list; every
// other view spans the paid orders too.
function needsPaidOrders() {
  return selectedBatch.value.id !== 'unpaid'
}

async function ensurePaidOrdersLoaded() {
  if (paidOrdersLoaded.value) return
  // Concurrent callers share one in-flight request
  if (paidOrdersRequest === null) paidOrdersRequest = LoadPaidGridData()
  await paidOrdersRequest
}

const columnReorder = (options: { columns: typeof columns.value }) => {
    columns.value = options.columns;
};

function sortChangeHandler (e: GridSortChangeEvent) {
  loader.value = true;
  if (e.sort[0] !== undefined) {
    sort.value = e.sort;
  }
  else
  {
    logger.debug('sortChangeHandler: e.sort[0] is undefined')
  }
  loader.value = false;
  HideNoRecordsWhileLoading()
}

function ReLoadGrid()
{
  loader.value = true;
  gridData.value = []
  origGridData.value = []
  // The cached paid list is deliberately kept: refetching it costs a request
  // slow enough to time out, and a full page load already clears it
  isFiltered.value = false
  sort.value = [{ field: 'orderTime', dir: 'desc' }]
  HideNoRecordsWhileLoading()
}

function HideNoRecordsWhileLoading()
{
  const eventListDiv = document.getElementById('orderListGridRowDiv') as HTMLElement
  const classAttribute = eventListDiv.attributes.getNamedItem('class');
  if (classAttribute) {
    if (loader.value == false)
    {
      classAttribute.value = 'row'
    }
    else
    {
      classAttribute.value = 'row  grid-loading'
    }
  }
}

function toggleActionsMenu(event: Event, dataItem: IOrderListItem) {
  // Prevent default behavior
  event.preventDefault()
  event.stopPropagation()

  // Set the active order
  activeOrderId.value = String(dataItem.aoeOrderId)
  activeOrderToken.value = dataItem.aoeOrderIdToken

  // Position the menu near the clicked button
  const target = event.currentTarget as HTMLElement
  const rect = target.getBoundingClientRect()
  menuPosition.value = {
    x: rect.left - 200, // Position to the left of the button
    y: rect.top // Position at the same height as the button
  }

  // Show the menu
  showActionsMenu.value = true

  // Add a click event listener to close the menu when clicking outside
  setTimeout(() => {
    document.addEventListener('click', closeActionsMenu)
  }, 0)
}

function closeActionsMenu() {
  showActionsMenu.value = false
  document.removeEventListener('click', closeActionsMenu)
}

async function redirectToRMA(aoeOrderId: string) {
  logger.debug('redirectToRMA', aoeOrderId)
  closeActionsMenu()
  const rmaEntryUrl = await GetRMAEntryUrl()
  window.location.href = `${rmaEntryUrl}?orderid=${aoeOrderId}`
}

function viewInvoice(aoeOrderIdToken: string) {
  logger.debug('viewInvoice')
  closeActionsMenu()
  const receiptURL = finalOrderSummaryURL.value + '?id=' + aoeOrderIdToken;
  window.location.href = receiptURL
}

function redirectToOrderSummary(event: MouseEvent, aoeOrderIdToken: string) {
    logger.debug('redirectToOrderSummary', aoeOrderIdToken)
    const receiptURL = finalOrderSummaryURL.value + '?id=' + aoeOrderIdToken;
    window.location.href = receiptURL
}

function getValueByPath<T>(obj: T, path: string): unknown {
  return path.split('.').reduce((o: unknown, key) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[key] : undefined), obj);
}

// The numeric and date filter inputs are undefined (not null) while empty, so
// a null-only check would let the column filter run with no criteria at all.
function hasColumnFilterValue() {
    return searchText.value.length > 0 ||
           txtDate.value !== undefined ||
           (searchNumber.value !== undefined && searchNumber.value !== null)
}

function allOrders() {
    return [...origGridData.value, ...paidGridData.value]
}

async function applyGridFilter() {
    logger.debug('applyGridFilter')

    filterExpanded.value = false
    // A narrower result set can be shorter than the current page offset
    skip.value = 0

    // Awaited before the filter runs, so a view that spans paid orders cannot
    // silently drop them while the request is still in flight
    if (needsPaidOrders()) await ensurePaidOrdersLoaded()

    isFiltered.value = true

    if (selectedBatch.value.id === 'paid') {
        gridData.value = [...paidGridData.value];
    }
    else if (selectedBatch.value.id === 'unpaid') {
        gridData.value = [...origGridData.value]
    }
    else if (selectedBatch.value.id === '') {
        gridData.value = allOrders()
    }
    else {
        // A batch holds both unpaid and already paid orders
        gridData.value = allOrders()
        gridData.value = gridData.value.filter(item => item.appPeriodDateGuidPayoutPeriod === Number(selectedBatch.value.id));
    }

    if (selectedOrderType.value.id !== '') {
        gridData.value = gridData.value.filter(item => item.orderTypeId === selectedOrderType.value.id);
    }

    if (selectedColumn.value.id !== '' && hasColumnFilterValue()) {
        const columnField = selectedColumn.value.id;
        const columnOperand = selectedColumnOperand.value.id;

        const search = searchText.value.length > 0 ? searchText.value : String(searchNumber.value);

        if (columnOperand === 'Equals') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField)

                return selectedColumn.value.id === "orderTime" ?
                new Date(value as string).getFullYear() === txtDate.value?.getFullYear() &&
                new Date(value as string).getMonth() === txtDate.value?.getMonth() &&
                new Date(value as string).getDate() === txtDate.value?.getDate()
                :  String(value).toLowerCase() === search.toLowerCase();
              }
            );
        }
        else if (columnOperand === 'NotEquals') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "orderTime" ?
                new Date(value as string).getFullYear() !== txtDate.value?.getFullYear() ||
                new Date(value as string).getMonth() !== txtDate.value?.getMonth() ||
                new Date(value as string).getDate() !== txtDate.value?.getDate()
                : String(value).toLowerCase() !== search.toLowerCase();
              }
            );
        }
        else if (columnOperand === 'GreaterThan') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "orderTime" && txtDate.value ?
                new Date(value as string).getFullYear() > txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() > txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() >= txtDate.value?.getMonth() && new Date(value as string).getDate() > txtDate.value?.getDate())
                : Number(value) > Number(search);
              }
            );
        }
        else if (columnOperand === 'GreaterThanOrEqual') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "orderTime" && txtDate.value ?
                new Date(value as string).getFullYear() > txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() > txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() >= txtDate.value?.getMonth() && new Date(value as string).getDate() >= txtDate.value?.getDate())
                : Number(value) >= Number(search);
              }
            );
        }
        else if (columnOperand === 'LessThan') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "orderTime" && txtDate.value ?
                new Date(value as string).getFullYear() < txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() <= txtDate.value?.getFullYear() && new Date(value as string).getMonth() < txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() <= txtDate.value?.getFullYear() && new Date(value as string).getMonth() <= txtDate.value?.getMonth() && new Date(value as string).getDate() < txtDate.value?.getDate())
                : Number(value) < Number(search);
              }
            );
        }
        else if (columnOperand === 'LessThanOrEqual') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "orderTime" && txtDate.value ?
                new Date(value as string).getFullYear() < txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() <= txtDate.value?.getFullYear() && new Date(value as string).getMonth() < txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() <= txtDate.value?.getFullYear() && new Date(value as string).getMonth() <= txtDate.value?.getMonth() && new Date(value as string).getDate() <= txtDate.value?.getDate())
                : Number(value) <= Number(search);
              }
            );
        }
        else if (columnOperand === 'BeginsWith') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return String(value).toLowerCase().startsWith(search.toLowerCase());
              }
            );
        }
        else if (columnOperand === 'Contains') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return String(value).toLowerCase().includes(search.toLowerCase());
              }
            );
        }
        else if (columnOperand === 'Between' &&
                 (searchSecondNumber.value !== undefined ||
                  (selectedColumn.value.id === 'orderTime' && txtSecondDate.value !== undefined))) {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "orderTime" && txtDate.value && txtSecondDate.value ?
                (new Date(value as string).getFullYear() > txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() > txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() >= txtDate.value?.getMonth() && new Date(value as string).getDate() >= txtDate.value?.getDate())) &&
                (new Date(value as string).getFullYear() < txtSecondDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() <= txtSecondDate.value?.getFullYear() && new Date(value as string).getMonth() < txtSecondDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() <= txtSecondDate.value?.getFullYear() && new Date(value as string).getMonth() <= txtSecondDate.value?.getMonth() && new Date(value as string).getDate() <= txtSecondDate.value?.getDate()))
                : Number(value) >= Number(search) && (typeof searchSecondNumber.value !== 'undefined' && Number(value) <= Number(searchSecondNumber.value));
              }
            );
        }
    }
}

// Put the filter drawer back in the state it has on first load
function resetFilterState() {
    // Back to the view the page opens on, which is what LoadGridData renders
    const defaultBatch = batchDDL.value.find(batch => batch.id === 'unpaid')
    selectedBatch.value = defaultBatch ?? { text: '', id: '' }
    selectedColumn.value = columnDDL.value.length > 0 ? columnDDL.value[0] : { text: '', id: '', type: '' }
    SetColumnOperandDDLValues()
    clearColumnFilterValues()
    isFiltered.value = false
    skip.value = 0
}

function clearColumnFilterValues() {
    searchText.value = ''
    searchNumber.value = undefined
    searchSecondNumber.value = undefined
    txtDate.value = undefined
    txtSecondDate.value = undefined
}

function clearGridFilter() {
    logger.debug('clearGridFilter')
    filterExpanded.value = false
    resetFilterState()
    LoadGridData()
}

function clearAllFilters() {
    resetFilterState()
    LoadGridData()
}

async function clearBatchFilter() {
    // batchDDL[0] is the "all views" entry, so the chip actually goes away
    selectedBatch.value = batchDDL.value.length > 0 ? batchDDL.value[0] : { text: '', id: '' }
    await applyRemainingFilters()
}

async function clearOrderTypeFilter() {
    // orderTypeDDL[0] is the "all order types" entry
    selectedOrderType.value = orderTypeDDL.value.length > 0 ? orderTypeDDL.value[0] : { text: '', id: '' }
    await applyRemainingFilters()
}

async function clearColumnFilter() {
    selectedColumn.value = columnDDL.value.length > 0 ? columnDDL.value[0] : { text: '', id: '', type: '' }
    SetColumnOperandDDLValues()
    clearColumnFilterValues()
    await applyRemainingFilters()
}

// Re-apply whatever is left after a chip was dismissed. applyGridFilter is the
// single source of truth for how the filters combine.
async function applyRemainingFilters() {
    // selectedBatch.id === '' spans every view, so there is nothing left to narrow
    if (!hasActiveFilter.value) {
        await ensurePaidOrdersLoaded()
        gridData.value = allOrders()
        isFiltered.value = true
        skip.value = 0
        return
    }
    await applyGridFilter()
}

function exportExcel() {
    const exportColumns = customizeColumns.filter(column => column.hidden == false && column.field !== 'actions');
    // sortedGridData is what actually gets exported, and gridData is empty
    // until a filter has been applied
    for (const item of sortedGridData.value) {
        item.orderTimeStr = formatDate(item.orderTime);
        item.orderTotalStr = FormatCurrency(item.total ?? 0, item.currencyId) ?? '';
        item.commissionableValueStr = FormatCurrency(item.commissionableValue ?? 0, item.currencyId) ?? '';
    }

    for (const column of exportColumns) {
        if (column.field === 'total') {
            column.field = 'orderTotalStr';
        }
        if (column.field === 'orderTime') {
            column.field = 'orderTimeStr';
        }
        if (column.field === 'commissionableValue') {
            column.field = 'commissionableValueStr';
        }
    }

    saveExcel({
        data: sortedGridData.value,
        fileName: t('orderList.ExportFileName'),
        columns: exportColumns
    });
}

function columnDrawerClick() {
  logger.debug('columnDrawerClick')
  expanded.value = !expanded.value
}

function gridFilterDrawerClick() {
  logger.debug('gridFilterDrawerClick')
  filterExpanded.value = !filterExpanded.value
}

function handleDragStart(e: ListBoxDragEvent) {
    draggedItem.value = e.dataItem;
}

function handleDrop(e: ListBoxDragEvent) {
    const result = processListBoxDragAndDrop(
        customizeColumns,
        [],
        draggedItem.value,
        e.dataItem,
        'field'
    );

    customizeColumns = result.listBoxOneData;
}

function columnChangeVisibility(event: CheckboxChangeEvent, field: string) {
    logger.debug('columnChangeVisibility', field, event.value)
    const column = customizeColumns.find(col => col.field === field);
    if (column) {
        column.hidden = !event.value;
    }
}

function orderTypeDLLChangeHandler(orderType: OrderType) {
  logger.debug('orderTypeDLLChangeHandler')
  selectedOrderType.value = orderType
}

function batchDLLChangeHandler(batch: Batch) {
  logger.debug('batchDLLChangeHandler')
  selectedBatch.value = batch
}

function columnDLLChangeHandler(column: GridColumn) {
  logger.debug('columnDLLChangeHandler')
  selectedColumn.value = column

  SetColumnOperandDDLValues()
}

function SetColumnOperandDDLValues()
{
  columnOperandDDL.value = []

  if (selectedColumn.value.id === '') {
    const columnOperandNeedColumn = { text: t('orderList.ColumnOperandSelectColumn'), id: '' }
    columnOperandDDL.value.push(columnOperandNeedColumn)
    selectedColumnOperand.value = columnOperandNeedColumn
    return
  }

  const columnOperandEquals = { text: t('orderList.ColumnOperandEquals'), id: 'Equals' }
  columnOperandDDL.value.push(columnOperandEquals)
  selectedColumnOperand.value = columnOperandEquals

  columnOperandDDL.value.push({ text: t('orderList.ColumnOperandNotEquals'), id: 'NotEquals' })

  if (selectedColumn.value.type === 'date' || selectedColumn.value.type === 'number') {
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandGreaterThan'), id: 'GreaterThan' })
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandGreaterThanOrEqual'), id: 'GreaterThanOrEqual' })
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandLessThan'), id: 'LessThan' })
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandLessThanOrEqual'), id: 'LessThanOrEqual' })
  }

  if (selectedColumn.value.type === 'string') {
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandBeginsWith'), id: 'BeginsWith' })
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandContains'), id: 'Contains' })
  }

  if (selectedColumn.value.type === 'date' || selectedColumn.value.type === 'number') {
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandBetween'), id: 'Between' })
  }
}

function columnOperandDLLChangeHandler(column: GridColumnOperand) {
  logger.debug('columnOperandDLLChangeHandler')
  selectedColumnOperand.value = column
}

function dateChangeHandler(event: DatePickerChangeEvent) {
  logger.debug('dateChangeHandler event:', event)
  if (event.value != null) {
    txtDate.value = event.value
  }
}

function secondDateChangeHandler(event: DatePickerChangeEvent) {
  logger.debug('secondDateChangeHandler event:', event)
  if (event.value != null) {
    txtSecondDate.value = event.value
  }
}

// Handle mobile pagination
function handleMobilePagination(newSkip: number) {
  skip.value = newSkip

  // Use a simpler approach - directly create a mock event object
  const mockEvent = {
    page: { skip: newSkip, take: take.value },
    event: {
      preventDefault: () => {}, // Add required EventTarget methods
      stopPropagation: () => {},
      target: document.createElement('button') // Use a real EventTarget
    }
  } as GridPageChangeEvent

  // Call pageChangeHandler with the mock event
  pageChangeHandler(mockEvent)
}

</script>

<template>
 <div>
  <div class="pageHeader pb-3 me-3">{{ t('orderList.Orders') }}</div>
  <div class="headerborder w-100">
    <div class="row p-2 align-items-center">
        <div class="d-flex align-items-center">
          <span class="pageHeader me-3">{{ t('orderList.AllOrders') }}</span>
          <div class="count-pill">{{ t('orderList.orderCount', { count: pageSortedGridData.length }) }}</div>
        </div>
        <div class="pageSubHeader">{{ t('orderList.OrderSubHeader') }}</div>
    </div>
    <div id="orderListExport" class="mt-3 py-2 d-flex justify-content-end flex-wrap w-100">
        <div class="search-container pl-2 ">
          <KTextBox
            v-model="searchQuery"
            @input="onSearchInput"
            :placeholder="t('orderList.searchPlaceholder')"
            class="search-input"
          />
        </div>
      <div v-if="hasActiveFilter" class="filter-indicators-container mr-2">
        <!-- Batch filter indicator -->
        <div v-for="filter in activeFilters" :key="filter.id" class="filter-indicator">
          <span class="filter-text">{{ filter.text }}</span>
          <KButton
            :svg-icon="xIcon"
            class="filter-clear-btn"
            @click="filter.type === 'batch' ? clearBatchFilter() : filter.type === 'orderType' ? clearOrderTypeFilter() : clearColumnFilter()"
            :title="t('orderList.clearFilter')"
          ></KButton>
        </div>
        <!-- Clear all button shows when multiple filters are active -->
        <div v-if="activeFilters.length > 1" class="filter-indicator clear-all-indicator">
          <span class="filter-text">{{ t('orderList.clearAllFilters') }}</span>
          <KButton
            :svg-icon="xIcon"
            class="filter-clear-btn"
            @click="clearAllFilters"
            :title="t('orderList.clearAllFilters')"
          ></KButton>
        </div>
      </div>
      <div class="order-list-column-icon-div pl-2 ">
        <KButton
          :svg-icon="tableColumnGroupsIcon"
          class="order-list-column-icon"
          @click="columnDrawerClick"
        ></KButton>
      </div>
      <div class=" order-list-column-icon-div px-2 ">
        <KButton
          :svg-icon="filterIcon"
          class="order-list-column-icon"
          @click="gridFilterDrawerClick"
        >{{ t('consultantCorner.contacts.filters') }}</KButton>
      </div>
      <div class=" order-list-export-div d-none pl-2 ">
        <KButton
          :theme-color="'checkoutContinue'"
          class="baseBtn primaryBtn order-list-button order-list-export-btn"
          @click="exportExcel"
        >{{ t('orderList.ExportButton') }}</KButton>
      </div>
    </div>
  </div>
    <div id="orderListGridRowDiv" class="row w-100 mx-0">
      <div class="col-12">
        <!-- Desktop Grid View -->
        <div class="d-none d-md-block">
          <LocalizationProvider :language="locale">
            <Grid
              :key="gridKey"
              ref="grid"
              :data-items="pageSortedGridData"
              :resizable="true"
              :sortable="true"
              :sort="sort"
              :pageable="true"
              :skip="skip"
              :take="take"
              :total="total"
              :columns="columns"
              @sortchange="sortChangeHandler"
              @pagechange="pageChangeHandler"
              :reorderable="true"
              @columnreorder="columnReorder"
              :loader="loader"
            >
          <template v-slot:orderIDTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="order-orderid-div">
              <a class="base-hyper-link order-title-link" href="javascript:void(0)" @click="redirectToOrderSummary($event, props.dataItem.aoeOrderIdToken)">{{ props.dataItem.aoeOrderId }}</a>
            </td>
          </template>
          <template v-slot:orderTotalTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'">
              {{ FormatCurrency(props.dataItem.total ?? 0, props.dataItem.currencyId) }}
            </td>
          </template>
          <template v-slot:orderCommissionableValueTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'">
              {{ FormatCurrency(props.dataItem.commissionableValue ?? 0, props.dataItem.currencyId) }}
            </td>
          </template>
          <template v-slot:orderDateTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="text-center">
              {{ formatDate(props.dataItem.orderTime) }}
            </td>
          </template>
          <template v-slot:orderTrackingNumberTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="text-center">
              <div v-if="props.dataItem.trackingNumber.length == 0" id="orderNoTrackingNumberDiv">
                  {{ t('orderList.NoTrackingNumber') }}
              </div>
              <div v-if="props.dataItem.trackingNumber.length > 0" id="orderTrackingNumberDiv">
                  <a
                    class=" base-hyper-link order-title-link"
                    :href="props.dataItem.trackingNumberUrl">
                    {{ props.dataItem.trackingNumber }}
                  </a>
              </div>
            </td>
          </template>
          <template v-slot:actionsHeaderTemplate>
            <th class="k-header order-grid-return-header text-center">
              <SvgIcon :icon="moreVerticalIcon" class="actions-header-icon" />
            </th>
          </template>
          <template v-slot:orderReturnTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="grid-return order-grid-return text-center">
              <KButton
                :fill-mode="'clear'"
                @click="toggleActionsMenu($event, props.dataItem)"
                :svg-icon="moreVerticalIcon"
                class="order-grid-return-icon"
              ></KButton>
            </td>
          </template>
        </Grid>
          </LocalizationProvider>
        </div>

        <!-- Mobile Card View -->
        <div class="d-block d-md-none mobile-card-view">
          <div v-if="loader" class="mobile-loader">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t('orderList.Loading') }}</span>
            </div>
          </div>
          <div v-else>
            <!-- Pagination Controls Top -->
            <div class="mobile-pagination d-flex justify-content-between align-items-center mb-3">
              <div class="page-info">
                {{ t('orderList.Page') }} {{ skip / take + 1 }} {{ t('orderList.Of') }} {{ Math.ceil(total / take) }}
              </div>
              <div class="pagination-controls">
                <KButton
                  :disabled="skip === 0"
                  @click="handleMobilePagination(Math.max(0, skip - take))"
                  :fill-mode="'clear'"
                  class="page-btn"
                >
                  &lt;
                </KButton>
                <KButton
                  :disabled="skip + take >= total"
                  @click="handleMobilePagination(skip + take)"
                  :fill-mode="'clear'"
                  class="page-btn"
                >
                  &gt;
                </KButton>
              </div>
            </div>

            <!-- Cards -->
            <div class="mobile-cards">
              <div v-for="(item, index) in pageSortedGridData" :key="index" class="mobile-card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <a class="order-id-link" href="javascript:void(0)" @click="redirectToOrderSummary($event, item.aoeOrderIdToken)">
                    {{ t('orderList.OrderId') }}: {{ item.aoeOrderId }}
                  </a>
                  <KButton
                    :fill-mode="'clear'"
                    @click="toggleActionsMenu($event, item)"
                    :svg-icon="moreVerticalIcon"
                    class="card-actions-btn"
                  ></KButton>
                </div>

                <div class="card-body">
                  <div class="card-row">
                    <div class="card-label">{{ t('orderList.OrderDate') }}:</div>
                    <div class="card-value">{{ formatDate(item.orderTime) }}</div>
                  </div>

                  <div class="card-row">
                    <div class="card-label">{{ t('orderList.OrderTotal') }}:</div>
                    <div class="card-value">{{ FormatCurrency(item.total ?? 0, item.currencyId) }}</div>
                  </div>

                  <div class="card-row">
                    <div class="card-label">{{ t('orderList.OrderType') }}:</div>
                    <div class="card-value">{{ item.orderTypeText }}</div>
                  </div>

                  <div class="card-row">
                    <div class="card-label">{{ t('orderList.LastName') }}:</div>
                    <div class="card-value">{{ item.customer.lastName }}</div>
                  </div>

                  <div class="card-row">
                    <div class="card-label">{{ t('orderList.Email') }}:</div>
                    <div class="card-value">{{ item.customer.email }}</div>
                  </div>

                  <div class="card-row">
                    <div class="card-label">{{ t('orderList.TrackingNumber') }}:</div>
                    <div class="card-value">
                      <div v-if="item.trackingNumber.length == 0">
                        {{ t('orderList.NoTrackingNumber') }}
                      </div>
                      <div v-else>
                        <a class="base-hyper-link order-title-link" :href="item.trackingNumberUrl">
                          {{ item.trackingNumber }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination Controls Bottom -->
            <div class="mobile-pagination d-flex justify-content-between align-items-center mt-3">
              <div class="page-info">
                {{ t('orderList.Page') }} {{ skip / take + 1 }} {{ t('orderList.Of') }} {{ Math.ceil(total / take) }}
              </div>
              <div class="pagination-controls">
                <KButton
                  :disabled="skip === 0"
                  @click="handleMobilePagination(Math.max(0, skip - take))"
                  :fill-mode="'clear'"
                  class="page-btn"
                >
                  &lt;
                </KButton>
                <KButton
                  :disabled="skip + take >= total"
                  @click="handleMobilePagination(skip + take)"
                  :fill-mode="'clear'"
                  class="page-btn"
                >
                  &gt;
                </KButton>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Drawer
        :expanded="expanded"
        :position="'end'"
        :mode="'overlay'"
        :class="'order-list-column-drawer'"
        @overlayclick="columnDrawerClick"
        :items="
          drawerItems.map((item, index) => ({
            ...item,
          }))
        "
        :item="'CustomItem'"
      >
        <template v-slot:CustomItem="{ props }">
          <DrawerItem v-bind="props" @click="props.onClick">
            <div class="aoe-cart-detail container">
              <div id="orderListColumHeader" class="row mt-3">
                <div class="col-12 order-list-column-header pageHeader">
                  {{ t('orderList.ColumnListHeader') }}
                </div>
              </div>
              <div id="orderListColumSubHeader" class="row mt-3">
                <div class="col-12 order-list-column-subheader pageSubSubHeader">
                  {{ t('orderList.ColumnListSubHeader') }}
                </div>
              </div>
              <div id="orderListColumSubHeader" class="row mt-3">
                <div class="col-12 order-list-columns">
                  <ListBox
                    class="order-list-column-listbox"
                    :data-items="customizeColumns"
                    :text-field="'title'"
                    :draggable="true"
                    @dragstart="handleDragStart($event)"
                    @drop="handleDrop($event)"
                    :item="'item'"
                  >
                    <template #item="{ props }">
                      <div class="order-list-column-item row mt-1">
                        <div class="col-6 order-list-column-title">
                          <KCheckBox
                            class="order-list-column-checkbox"
                            :checked=!props.dataItem.hidden
                            :label=props.dataItem.title
                            @change="columnChangeVisibility($event, props.dataItem.field)"
                          >
                          </KCheckBox>
                        </div>
                        <div class="col-6 order-list-column-icon">
                          <SvgIcon :icon="arrowsTopBottomIcon" />
                        </div>
                      </div>
                    </template>
                  </ListBox>
                </div>
              </div>
            </div>
          </DrawerItem>
        </template>
      </Drawer>
      <Drawer
        :expanded="filterExpanded"
        :position="'end'"
        :mode="'overlay'"
        :class="'order-list-filter-drawer'"
        @overlayclick="gridFilterDrawerClick"
        :items="
          filterDrawerItems.map((item, index) => ({
            ...item,
          }))
        "
        :item="'CustomItem'"
      >
        <template v-slot:CustomItem="{ props }">
          <DrawerItem v-bind="props" @click="props.onClick">
            <div class="aoe-cart-detail container">
              <div id="orderListFilterHeader" class="row mt-3">
                <div class="col-12 order-list-filter-header pageHeader">
                  {{ t('orderList.GridFilterHeader') }}
                </div>
              </div>
              <div id="orderListFilterSubHeader" class="row mt-3">
                <div class="col-12 order-list-filter-subheader pageSubSubHeader">
                  {{ t('orderList.GridFilterSubHeader') }}
                </div>
              </div>
              <div id="orderListFilterByStatus" class="row mt-3">
                <div class="col-12 order-list-filters">
                    {{ t('orderList.FilterByView') }}
                </div>
              </div>
              <div id="orderListFilterByStatusDDL" class="row mt-3">
                <div class="col-12 order-list-filters">
                    <DropDownList
                      id="batchDDL"
                      :data-items='batchDDL'
                      :value= "selectedBatch"
                      :text-field="'text'"
                      :data-item-key="'id'"
                      @change="batchDLLChangeHandler($event.target.value)">
                    </DropDownList>
                </div>
              </div>
              <div id="orderListFilterByOrderType" class="row mt-3">
                <div class="col-12 order-list-filters">
                    {{ t('orderList.FilterByOrderType') }}
                </div>
              </div>
              <div id="orderListFilterByOrderTypeDDL" class="row mt-3">
                <div class="col-12 order-list-filters">
                    <DropDownList
                      id="orderTypeDDL"
                      :data-items='orderTypeDDL'
                      :value= "selectedOrderType"
                      :text-field="'text'"
                      :data-item-key="'id'"
                      @change="orderTypeDLLChangeHandler($event.target.value)">
                    </DropDownList>
                </div>
              </div>
              <div id="orderListFilterByColumn" class="row mt-3">
                <div class="col-12 order-list-filters">
                    {{ t('orderList.FilterByColumn') }}
                </div>
              </div>
              <div id="orderListFilterByColumnDDL" class="row mt-3">
                <div class="col-12 order-list-filters">
                    <DropDownList
                      id="columnDDL"
                      :data-items='columnDDL'
                      :value= "selectedColumn"
                      :text-field="'text'"
                      :data-item-key="'id'"
                      @change="columnDLLChangeHandler($event.target.value)">
                    </DropDownList>
                </div>
              </div>
              <div id="orderListFilterByColumnOperandDDL" class="row mt-1">
                <div class="col-12 order-list-filters">
                    <DropDownList
                      id="columnOperandDDL"
                      :data-items='columnOperandDDL'
                      :value= "selectedColumnOperand"
                      :text-field="'text'"
                      :data-item-key="'id'"
                      @change="columnOperandDLLChangeHandler($event.target.value)">
                    </DropDownList>
                </div>
              </div>
              <div id="orderListFilterByColumnOperandDDL" class="row mt-1">
                <div v-if="selectedColumn.id != 'orderTime' && selectedColumn.id != 'total' && selectedColumn.id != 'aoeOrderId' && selectedColumn.id != 'aoeMasterOrderId'" class="col-12 order-list-filters">
                    <KTextBox
                      :rounded="'large'"
                      :theme-color="'primary'"
                      v-model="searchText"
                      :placeholder="t('orderList.PlaceHolderSearchFilter')"
                    ></KTextBox>
                </div>
                <div v-if="selectedColumn.id == 'orderTime'" class="col-12 order-list-filters">
                    <LocalizationProvider :language="locale">
                      <DatePicker
                        :value="txtDate"
                        @change="dateChangeHandler($event)"
                        :placeholder="t('orderList.PlaceHolderSearchFilterDate')"
                        :format="dateFormat"
                        :formatPlaceholder="formatPlaceholder">
                      </DatePicker>
                    </LocalizationProvider>
                </div>
                <div v-if="selectedColumn.id == 'total' || selectedColumn.id == 'aoeOrderId' || selectedColumn.id == 'aoeMasterOrderId'" class="col-12 order-list-filters">
                  <KNumericTextBox
                      :rounded="'large'"
                      :theme-color="'primary'"
                      v-model="searchNumber"
                      :placeholder="t('orderList.PlaceHolderSearchFilterNumber')"
                  ></KNumericTextBox>
                </div>
                <div v-if="selectedColumnOperand.id == 'Between'" class="col-12 order-list-filters">
                  {{ t('orderList.BetweenAnd') }}
                </div>
                <div v-if="selectedColumnOperand.id == 'Between' && selectedColumn.id == 'orderTime'" class="col-12 order-list-filters">
                    <LocalizationProvider :language="locale">
                      <DatePicker
                        :value="txtSecondDate"
                        @change="secondDateChangeHandler($event)"
                        :placeholder="t('orderList.PlaceHolderSearchFilterSecondDate')"
                        :format="dateFormat"
                        :formatPlaceholder="formatPlaceholder">
                      </DatePicker>
                    </LocalizationProvider>
                </div>
                <div v-if="selectedColumnOperand.id == 'Between' && (selectedColumn.id == 'total' || selectedColumn.id == 'aoeOrderId' || selectedColumn.id == 'aoeMasterOrderId')" class="col-12 order-list-filters">
                  <KNumericTextBox
                      :rounded="'large'"
                      :theme-color="'primary'"
                      v-model="searchSecondNumber"
                      :placeholder="t('orderList.PlaceHolderSearchFilterSecondNumber')"
                  ></KNumericTextBox>
                </div>
              </div>
              <div id="orderListFilterByColumnOperandDDL" class="row mt-3">
                <div class="col-6 order-list-filter-buttons text-center">
                    <KButton
                      :theme-color="'checkoutContinue'"
                      class="baseBtn primaryBtn order-list-button order-list-apply-filter-btn"
                      @click="applyGridFilter"
                    >{{ t('orderList.ApplyFilterButton') }}</KButton>
                </div>
                <div class="col-6 order-list-filter-buttons text-center">
                    <KButton
                      :theme-color="'checkoutContinue'"
                      class="baseBtn secondaryBtn order-list-button order-list-clear-filter-btn"
                      @click="clearGridFilter"
                    >{{ t('orderList.ClearFilterButton') }}</KButton>
                </div>
              </div>
            </div>
          </DrawerItem>
        </template>
      </Drawer>

      <!-- Actions Popup Menu -->
      <div v-if="showActionsMenu" class="actions-menu" :style="{ left: menuPosition.x + 'px', top: menuPosition.y + 'px' }">
        <div class="actions-menu-item" @click="redirectToRMA(activeOrderId)">
          <SvgIcon :icon="redoIcon" class="actions-menu-icon" /> {{ t('orderList.CreateRMA') }}
        </div>
        <div class="actions-menu-item" @click="viewInvoice(activeOrderToken)">
          <SvgIcon :icon="fileTxtIcon" class="actions-menu-icon" /> {{ t('orderList.ViewInvoice') }}
        </div>
      </div>
    </div>
 </div>
</template>

    <style lang="scss">
    .search-input {
      width: 300px;
    }

    .count-pill {
      background-color: var(--vt-c-teal-dark);
      color: white;
      padding: $spacing-1 $spacing-3;
      @include typography-small;
      @include font-weight-medium;
      border-radius: $radius-xl;
    }

    .actions-menu {
      position: fixed;
      background-color: white;
      border: 1px solid #e0e0e0;
      border-radius: $radius-sm;
      box-shadow: 0 2px 8px rgba(black, 0.2);
      z-index: $z-notification;
      width: 200px;
      transform: translateY(0);
    }

    .actions-menu-item {
      padding: $spacing-2 $spacing-4;
      cursor: pointer;
      display: flex;
      align-items: center;
    }

    .actions-menu-item:hover {
      background-color: var(--vt-c-white-soft);
    }

    .actions-menu-icon {
      @include typography-body;
      margin-right: 8px;
      color: var(--vt-c-teal-dark);
    }

    .actions-header-icon {
      @include typography-body;
      color: var(--vt-c-teal-dark);
    }

.grid-loading .k-grid-norecords .k-table-td
{
  content-visibility: hidden;
  height: 50px;
}

.order-grid-return-header .k-column-title
{
  width: 100%;
}

.order-grid-return-header .k-link
{
  text-align: center;
}

.order-list-column-icon-div .k-svg-icon,
.order-list-column-icon-div .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.order-list-column-icon-div .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg
.order-grid-return .k-svg-icon,
.order-grid-return .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.order-grid-return .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg
{
    width: 20px;
    height: 20px;
}

.order-list-column-icon-div .k-button-md.k-icon-button
{
    border: solid 1px #cccccc;
    border-radius: 10%;
    padding: $spacing-2-5;
}

.order-list-button
{
  padding: $spacing-2-5;
}

.order-list-column-listbox
{
  width: 100%;
  height: 100%;
}

.order-list-column-icon
{
  text-align: right;
}

.order-list-column-item
{
  border: solid 1px #cccccc;
  border-radius: 10%;
  width: 100%;
  margin: $spacing-2-5;
  padding: $spacing-1
}

.order-list-column-title .k-checkbox {
  border: solid 1px black !important;
}

.order-list-column-title .k-checkbox-label
{
  margin-left: 15px;
}
.pl-2{
  padding-left: 0.5rem;
}
.grid-return button.order-grid-return-icon{
  float:right;
  padding-right:6px;
  margin:unset;
  width:unset;
}
.order-grid-return-header{float:right;}

.search-container .k-textbox.k-input {
  padding-top: 3px !important;
  padding-bottom: 3px !important;
}
.headerborder{
  border: 1px solid var(--kendo-color-border, black);

}
.aoe-consultant-orders .k-grid {
  border-radius: $radius-none !important
}
#orderListExport {border-top: 1px solid var(--kendo-color-border, black);}
</style>
<style lang="scss" scoped>
.search-container :deep(.k-textbox.k-input) {
  padding-top: 3px !important;
  padding-bottom: 3px !important;
}

/* Filter indicator styles */
.filter-indicators-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $gap-sm;
  margin-left: 8px;
}

.filter-indicator {
  display: flex;
  align-items: center;
  background-color:white;
  border: 1px solid var(--kendo-color-border, black);
  border-radius: $radius-sm;
  padding: $spacing-1 $spacing-2;
  max-width: 200px;
}

.filter-text {
  @include typography-small;
  color: black;
  margin-right: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.filter-clear-btn {
  padding: $spacing-1;
  min-width: auto;
  height: auto;
  background: transparent;
  border: none;
  color: grey;
  cursor: pointer;
}

.filter-clear-btn:hover {
  color: #004999;
  background-color: rgba(black, 0.1);
}

/* Clear all indicator has a different style */
.clear-all-indicator {
  background-color: var(--vt-c-white-soft);
  border: 1px solid #cccccc;
  display:none;
}

.clear-all-indicator .filter-text {
  @include font-weight-medium;
  color: #555555;
}

/* !important required: Override Kendo's inline style width with responsive min() — matches the proven cart-summary drawer pattern, keeps a visible/clickable overlay strip at all breakpoints */
/* :deep() required — this is scoped CSS, and .k-drawer-wrapper is rendered internally by Kendo, not authored in this template */
:deep(.order-list-column-drawer .k-drawer-wrapper) {
  width: min(80vw, 420px) !important;
}

:deep(.order-list-filter-drawer .k-drawer-wrapper) {
  width: min(80vw, 420px) !important;
}

/* Mobile responsive adjustments */
@media (max-width: 767px) {
  /* Ensure the main container takes full width */
  .headerborder,
  #orderListGridRowDiv,
  #orderListExport {
    width: 100% !important;
    max-width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  /* Ensure the grid takes full width */
  .aoe-consultant-orders .k-grid {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* Adjust search and filter containers for mobile */
  .search-container {
    width: 100%;
    margin-bottom: 8px;
  }

  .search-input {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* Improve filter indicators display on small screens */
  .filter-indicators-container {
    width: 100%;
    margin-left: 0;
    margin-bottom: 8px;
  }

  /* Stack filter buttons vertically on mobile to prevent overlap */
  .order-list-filter-buttons.col-6 {
    width: 100%;
    max-width: 100%;
    flex: 0 0 100%;
    margin-bottom: $spacing-2;
  }

  .order-list-filter-buttons.col-6:last-child {
    margin-bottom: 0;
  }

  /* Mobile Card View Styles */
  .mobile-card-view {
    padding: 0 8px;
  }

  .mobile-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }

  .mobile-cards {
    display: flex;
    flex-direction: column;
    gap: $gap-md;
  }

  .mobile-card {
    background-color: white;
    border-radius: $radius-md;
    box-shadow: 0 2px 8px rgba(black, 0.1);
    overflow: hidden;
  }

  .mobile-card .card-header {
    background-color: var(--vt-c-white-soft);
    padding: $spacing-3 $spacing-4;
    border-bottom: 1px solid #e9ecef;
  }

  .mobile-card .order-id-link {
    @include font-weight-semibold;
    color: var(--vt-c-teal-dark);
    text-decoration: none;
  }

  .mobile-card .card-body {
    padding: $spacing-4;
  }

  .mobile-card .card-row {
    display: flex;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }

  .mobile-card .card-label {
    @include font-weight-medium;
    min-width: 120px;
    flex: 0 0 120px;
  }

  .mobile-card .card-value {
    flex: 1;
    word-break: break-word;
  }

  .mobile-card .card-actions-btn {
    padding: $spacing-1;
  }

  .mobile-pagination {
    padding: $spacing-2 0;
  }

  .mobile-pagination .page-info {
    @include typography-small;
    color: #6c757d;
  }

  .mobile-pagination .page-btn {
    min-width: 36px;
    height: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    @include font-weight-bold;
    justify-content: center;
  }
}
</style>
